//Dependencies
import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
  useLayoutEffect,
  useEffect,
  ChangeEventHandler,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useFilterKeyboardShortcuts } from '../../../../hooks/useFilterKeyboardShortcuts';
import { useTableCurrentPage } from '../../../../hooks/useTableCurrentPage';
import {
  createColumnHelper,
  ColumnFiltersState,
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  flexRender,
  getPaginationRowModel,
  PaginationState,
  VisibilityState,
  FilterFnOption,
  Row,
  getSortedRowModel,
  SortingState,
} from '@tanstack/react-table';
import { format, parseISO, formatISO } from 'date-fns';
import { useSelector, useDispatch } from 'react-redux';
import isEqual from 'lodash.isequal';

// Styled Components
import {
  TrStyled,
  TableContainerStyled,
  SubContainerFiltersStyled,
  TdStyled,
  SubContainerHead,
  ShowFiltersButton,
  ArrowBottomStyled,
  ContainerButtonStyled,
  CampaignSearchContainer,
  ContainerStyled,
  FiltersWithLoadingStyled,
  ContainerLoader,
  StyledTypography,
} from './styles';
import {
  Th,
  Tbody,
  Title,
  Table,
  TextCell,
  TextHeader,
  InputRadioContainer,
  InputRadioStyled,
  InputCalendarStyled,
  SearchableDropdownStyled,
} from '../components/MainStyledComponents';

import { DoubleTapRow } from '../components/DoubleTapRow';

// Components
import { PaginationButtons } from '../components/PaginationButtons';
import { DropDownVisibilityColumn } from '../components/DropDownVisibilityColumn';
import { SpecialTag } from '../../SpecialTag';
import { ButtonIconBackground } from '../../../inputs/buttons/ButtonIconBackground';

// Types
import {
  columnID,
  columnNames,
  getColumnId,
  getColumnNames,
} from '../components/types';
import { tableProps, dataTable, option } from './types';
import {
  onChangeValue,
  Options,
} from '../../../inputs/DropDown/DropDownList/types';
import { ImperativeRefInput } from '../../../inputs/DropDown/DropDownInput/types';
import {
  ImperativeRefCalendarInput,
  onChangeInputType,
} from '../../../inputs/Calendar/types';
import { variant, size } from '../../SpecialTag/types';
import { Order } from '../../../../types/orders';
import { tableIds, takeBase } from '../types';
import { ImperativeSearchBarInput } from '../../../inputs/SearchBar/types';
import InputPK from '../../../inputs/InputPK';
import {
  ImperativeRefInputPK,
  onChangeInputPKTypes,
} from '../../../inputs/InputPK/types';
import { ImperativeRefSearchableDropdown } from '../../../inputs/SearchableDropdown/types';

// Mocks
import { mocksTable } from './mocks';

// Utils
import { roidFilterFunction, dateFilterFunction } from './utils';
import { formatRoidForDisplay } from '../utils/roidFormatter';

// Media
import {
  useIsLandscapeTablet,
  useIsPortraitTablet,
} from '../../../../hooks/useMediaBreakPoints';
import DropDownInput from '../../../inputs/DropDown/DropDownInput';
import MultiSelectDropdown from '../../../inputs/DropDown/MultiSelectDropdown';
import { ImperativeRefMultiSelect } from '../../../inputs/DropDown/MultiSelectDropdown/types';
import SearchBar from '../../../inputs/SearchBar';
import { getStateOptionsByRoleAndEntityType } from '../../../../utils/order';
import { TABLE_ENTITY_TYPES, listingFiltersByRole } from '../types';
import { Role } from '../../../../types/auth';

// Data
import { getTrackOptions } from '../../../../utils/catalogLabels';

// Redux
import { getCursor } from '../../../../state/selectors/ui/tables';
import { useMirroredCursor } from '../utils/useMirroredCursor';
import { setCursor } from '../../../../state/actions/ui/cursorTables';
import { Loader } from '../../../inputs/Loader';
import { selectedRowModalTable } from '../../../../state/actions/ui/selectRowTableModal';

/**
 * Tarea #11 — Esta tabla NO usa `useServerPageSize`.
 *
 * Consumers (solo modales): `RelatesCommunicationModal`, `TransferSubOrderModal`.
 * Aunque la tabla recibe `tableId` y dispatch a `setCursor`, el `take` que va a
 * los queries NO depende de un selector de usuario sino de `pageSizeModal`
 * (responsivo: 5/7/8 según viewport del modal). El selector 25/50/100 no aplica
 * en modales por la regla del proyecto: la UI del modal está dimensionada
 * fija, no escala con la preferencia global.
 *
 * Si en el futuro algún modal necesita un selector, no extiendas este componente
 * — extrae primero el modal a su propia tabla server-side y migra ahí. Mantener
 * el modal con responsive sizing es la decisión actual.
 */
export const RelatedCampaignTable = ({
  titleText,
  data,
  setRowState,
  onReFetchData,
  onReFetchPagination,
  tableId,
  showButtonsPagination = true,
  setPageSize,
  namingPersistForm,
  onDoubleTapAccept,
  disabledRowStates,
  ...props
}: tableProps): JSX.Element => {
  const { t } = useTranslation(['tables', 'common', 'forms']);
  const translatedColumnNames = useMemo(() => getColumnNames(t), [t]);
  const dataAll = data?.campaigns ?? mocksTable;
  const roleActive = data?.roleActive;
  const globalCursor = useSelector(getCursor(tableId), isEqual);
  const dispatch = useDispatch();
  const inputListConRef = useRef<ImperativeRefInput>(null);
  const inputStateRef = useRef<ImperativeRefInput>(null);
  const inputDateRef = useRef<ImperativeRefCalendarInput>(null);
  const inputNameRef = useRef<ImperativeSearchBarInput>(null);
  const inputTeamRef = useRef<ImperativeRefMultiSelect>(null);
  const inputTrackRef = useRef<ImperativeRefInput>(null);
  const inputPKInitRef = useRef<ImperativeRefInputPK>(null);
  const inputPKEndRef = useRef<ImperativeRefInputPK>(null);
  const inputListPredefinedRef = useRef<ImperativeRefSearchableDropdown>(null);

  const isLandscapeTablet = useIsLandscapeTablet();
  const isPortraitTablet = useIsPortraitTablet();

  const [isLoading, setIsLoading] = useState(data.loading);
  const [showFilters, setShowFilters] = useState(false);
  const [rowSelection, setRowSelection] = useState({});
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  // C-11.7: persistido en Redux para mantener coherencia paginador↔cursor
  // al navegar a un detail y volver. Ver hooks/useTableCurrentPage.
  const [currentServerPage, setCurrentServerPage] = useTableCurrentPage(
    tableId as string,
  );
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: takeBase,
  });

  const [selectorCursor, setSelectorCursor] = useMirroredCursor<{
    cursor: string;
    take: number;
    indexType: number | undefined;
    indexExpedition: number | undefined;
    concessionId: string | undefined;
    fromDate: string | undefined;
    toDate: string | undefined;
    classification: string | undefined;
    name: string | undefined;
    state: string | undefined;
    teamIds: string[] | undefined;
    predefinedOrderId: string | undefined;
    pkInitKm: number | undefined;
    pkInitMeter: number | undefined;
    pkEndKm: number | undefined;
    pkEndMeter: number | undefined;
    track: string | undefined;
  }>(globalCursor);

  const [selectorData, setSelectorData] = useState<{
    indexType: number | undefined;
    indexExpedition: number | undefined;
    concessionId: string | undefined;
    fromDate: string | undefined;
    toDate: string | undefined;
    classification: string | undefined;
    name: string | undefined;
    state: string | undefined;
    teamIds: string[] | undefined;
    predefinedOrderId: string | undefined;
    pkInitKm: number | undefined;
    pkInitMeter: number | undefined;
    pkEndKm: number | undefined;
    pkEndMeter: number | undefined;
    track: string | undefined;
  }>({
    indexType: undefined,
    indexExpedition: undefined,
    concessionId: undefined,
    fromDate: undefined,
    toDate: undefined,
    classification: undefined,
    name: undefined,
    state: undefined,
    teamIds: undefined,
    predefinedOrderId: undefined,
    pkInitKm: undefined,
    pkInitMeter: undefined,
    pkEndKm: undefined,
    pkEndMeter: undefined,
    track: undefined,
  });

  const pageSizeModal = isPortraitTablet
    ? showFilters
      ? 5
      : 8
    : isLandscapeTablet
      ? showFilters
        ? 5
        : 7
      : 8;

  const takePagination =
    tableId === tableIds.CAMPAIGNS ? pageSize : pageSizeModal;

  const handleClick = useCallback(() => {
    setShowFilters(!showFilters);
  }, [showFilters]);

  useEffect(() => {
    isLandscapeTablet ? setShowFilters(false) : setShowFilters(true);
  }, [isLandscapeTablet]);

  // NOTE: Default state filter removed from RelatedCampaignTable
  // Related Campaigns should show all records without default state filter.
  // Only Generic WorkOrders tables should apply default state filter.
  // @see BUG #9 fix - entityStateFiltering.ts

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  useLayoutEffect(() => {
    setPageSize?.(pageSizeModal);
  }, [pageSizeModal]);

  // Sync responsive pageSize to Redux cursor on mount/resize
  useEffect(() => {
    if (tableId && takePagination && takePagination !== globalCursor?.take) {
      dispatch(setCursor(tableId as string, { ...globalCursor, take: takePagination }));
    }
  }, [takePagination]);

  useEffect(() => {
    const concessions = data?.filters?.concessions;
    setColumnVisibility(prev => ({
      ...prev,
      concession: !concessions || concessions.length > 1,
    }));
  }, [data?.filters?.concessions]);

  const [sizeTag, setSizeTag] = useState<size>(
    isPortraitTablet ? 'circle' : 'small'
  );

  useLayoutEffect(() => {
    setSizeTag(isPortraitTablet ? 'circle' : 'small');
  }, [isPortraitTablet]);

  useEffect(() => {
    setIsLoading(data.loading);
  }, [data]);

  const columnHelper = createColumnHelper<dataTable>();

  const [sorting] = useState<SortingState>([
    { id: 'creationDate', desc: true },
  ]);
  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'select',
        cell: ({ row }) => (
          <InputRadioContainer
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
          >
            <InputRadioStyled
              name="inputRadio"
              {...{
                checked: row.getIsSelected(),
                disabled: !row.getCanSelect(),
                indeterminate: row.getIsSomeSelected(),
                onChange: row.getToggleSelectedHandler(),
              }}
            />
          </InputRadioContainer>
        ),
      }),
      columnHelper.accessor(getColumnId(translatedColumnNames.state.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {translatedColumnNames.state.label}
          </TextHeader>
        ),
        cell: (info) => (
          <SpecialTag sizeTag={sizeTag} variant={info.getValue() as variant} />
        ),
      }),
      columnHelper.accessor(getColumnId(translatedColumnNames.roid.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {translatedColumnNames.roid.label}
          </TextHeader>
        ),
        cell: (info) => (
          <TextCell size={'S'}>
            {formatRoidForDisplay(info.getValue())}
          </TextCell>
        ),
        filterFn: 'roidFilter' as FilterFnOption<Order>, //Indicate the filter function to be used for this column
      }),
      columnHelper.accessor(
        getColumnId(translatedColumnNames.registerInitDate.id),
        {
          header: () => (
            <TextHeader size={'XS'} colorText={'greyDark'}>
              {translatedColumnNames.registerInitDate.label}
            </TextHeader>
          ),
          cell: (info) =>
            info.getValue() && (
              <TextCell size={'S'}>
                {format(
                  new Date(info.getValue() as string),
                  'dd/MM/yyyy - HH:mm'
                )}
              </TextCell>
            ),
          filterFn: 'dateFilter' as FilterFnOption<dataTable>,
        }
      ),

      columnHelper.accessor(getColumnId(translatedColumnNames.name.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {translatedColumnNames.name.label}
          </TextHeader>
        ),
        cell: (info) => (
          <TextCell size={'S'}>{info.getValue() as string}</TextCell>
        ),
        filterFn: 'includesString',
      }),
      columnHelper.accessor(getColumnId(translatedColumnNames.predefinedOrder.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {translatedColumnNames.predefinedOrder.label}
          </TextHeader>
        ),
        cell: (info) => (
          <TextCell size={'S'}>{info.getValue() as string}</TextCell>
        ),
      }),
      columnHelper.accessor(getColumnId(translatedColumnNames.concession.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {translatedColumnNames.concession.label}
          </TextHeader>
        ),
        cell: (info) => (
          <TextCell size={'S'}>{info.getValue() as string}</TextCell>
        ),
      }),
      columnHelper.accessor(getColumnId(translatedColumnNames.team.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {translatedColumnNames.team.label}
          </TextHeader>
        ),
        cell: (info) => (
          <TextCell size={'S'}>{(info.getValue() as string) || ''}</TextCell>
        ),
      }),
      columnHelper.accessor(getColumnId(translatedColumnNames.road.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {translatedColumnNames.road.label}
          </TextHeader>
        ),
        cell: (info) => (
          <TextCell size={'S'}>{info.getValue() as string}</TextCell>
        ),
      }),
      columnHelper.accessor(getColumnId(translatedColumnNames.track.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {translatedColumnNames.track.label}
          </TextHeader>
        ),
        cell: (info) => (
          <TextCell size={'S'}>{info.getValue() as string}</TextCell>
        ),
      }),
      columnHelper.accessor(getColumnId(translatedColumnNames.direction.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {translatedColumnNames.direction.label}
          </TextHeader>
        ),
        cell: (info) => (
          <TextCell size={'S'}>{info.getValue() as string}</TextCell>
        ),
      }),
      columnHelper.accessor(getColumnId(translatedColumnNames.pkInit.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {translatedColumnNames.pkInit.label}
          </TextHeader>
        ),
        cell: (info) => (
          <TextCell size={'S'}>{`${info.getValue()} km`}</TextCell>
        ),
      }),
      columnHelper.accessor(getColumnId(translatedColumnNames.pkEnd.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {translatedColumnNames.pkEnd.label}
          </TextHeader>
        ),
        cell: (info) => (
          <TextCell size={'S'}>{`${info.getValue()} km`}</TextCell>
        ),
      }),
      columnHelper.accessor(getColumnId(translatedColumnNames.description.id), {
        header: () => (
          <TextHeader size={'XS'} colorText={'greyDark'}>
            {translatedColumnNames.description.label}
          </TextHeader>
        ),
        cell: (info) => (
          <TextCell size={'S'}>{info.getValue() as string}</TextCell>
        ),
      }),
      columnHelper.accessor(
        getColumnId(translatedColumnNames.creationDate.id),
        {
          header: () => (
            <TextHeader size={'XS'} colorText={'greyDark'}>
              {translatedColumnNames.creationDate.label}
            </TextHeader>
          ),
          cell: (info) =>
            info.getValue() && (
              <TextCell size={'S'}>
                {format(
                  new Date(info.getValue() as string),
                  'dd/MM/yyyy - HH:mm'
                )}
              </TextCell>
            ),
        }
      ),
    ],
    [isPortraitTablet, sizeTag, translatedColumnNames]
  );

  const pagination = useMemo(
    () => ({
      pageIndex,
      pageSize,
    }),
    [pageIndex, pageSize]
  );

  const filterFns = {
    roidFilter: roidFilterFunction,
    dateFilter: dateFilterFunction,
  };

  const table = useReactTable({
    getRowId: (row: any) => row.id,
    data: dataAll,
    columns,
    state: {
      rowSelection,
      columnFilters,
      pagination,
      columnVisibility,
      sorting,
    },
    filterFns,
    getSortedRowModel: getSortedRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    enableRowSelection: disabledRowStates
      ? (row) => !disabledRowStates.includes(row.original.state ?? '')
      : true,
    enableMultiRowSelection: false,
    onPaginationChange: setPagination,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getPaginationRowModel: getPaginationRowModel(),
  });

  const onChangeCampaign: ChangeEventHandler<HTMLInputElement> = useCallback(
    async (event) => {
      setSelectorData((prevData) => {
        const newData = prevData;
        newData.name = event.target.value;
        return newData;
      });
    },
    [
      takePagination,
      selectorCursor,
      selectorData?.concessionId,
      selectorData.toDate,
      selectorData.fromDate,
      selectorData.name,
    ]
  );

  /**
   * *Function for filtering date ranges
   * @param range is a range of dates ({from, to})
   */

  const onChangeDateInput = useCallback<onChangeInputType>(
    (range) => {
      if (range.to) {
        range.to.setHours(23);
        range.to.setMinutes(59);
        range.to.setSeconds(59);
        range.to.setMilliseconds(999);
      }
      const fromDateIso = formatISO(range.from as Date);
      const toDateIso = formatISO(range.to as Date);
      setSelectorData((prevData) => {
        const newData = prevData;
        newData.fromDate = fromDateIso;
        newData.toDate = toDateIso;
        return newData;
      });
    },
    [
      takePagination,
      selectorCursor,
      selectorData?.concessionId,
      selectorData.state,
      selectorData.name,
    ]
  );

  const onChangeSelectedConcession = useCallback(
    (option: option) => {
      setSelectorData((prevData) => {
        const newData = prevData;
        newData.concessionId = option.value;
        return newData;
      });
    },
    [
      takePagination,
      selectorCursor,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData.state,
      selectorData.name,
    ]
  );

  const onChangeSelectedState = useCallback(
    async (option: option) => {
      setSelectorData((prevData) => {
        const newData = prevData;
        newData.state = option.value;
        return newData;
      });
    },
    [
      takePagination,
      selectorCursor,
      selectorData.fromDate,
      selectorData.toDate,
      selectorData.concessionId,
      selectorData.name,
    ]
  );

  const concessionOptions = data?.filters?.concessions?.map((concession) => ({
    label: concession.name,
    value: concession.id,
  }));

  const teamOptions = data?.filters?.teams?.map((team) => ({
    label: team.name,
    value: team.id,
  }));

  const trackOptions = useMemo(() => getTrackOptions(t), [t]);

  const predefinedOrderOptions = data?.filters?.predefinedOrders?.map(
    (predefined) => ({
      label: predefined.name,
      value: predefined.id,
    })
  );

  const onChangeSelectedTeam = useCallback(
    (values: string[]) => {
      const sorted = [...values].sort();
      const nextTeamIds = sorted.length > 0 ? sorted : undefined;
      setSelectorData((prevData) => ({ ...prevData, teamIds: nextTeamIds }));
    },
    []
  );

  const onChangeSelectedTrack = useCallback(
    (option: option) => {
      setSelectorData((prevData) => {
        const newData = prevData;
        newData.track = option.value;
        return newData;
      });
    },
    [
      takePagination,
      selectorCursor,
      selectorData.fromDate,
      selectorData.toDate,
      selectorData.concessionId,
      selectorData.name,
      selectorData.state,
      selectorData.teamIds,
    ]
  );

  const onChangeSelectedPredefinedOrder = useCallback(
    (option: option) => {
      setSelectorData((prevData) => {
        const newData = prevData;
        newData.predefinedOrderId = option.value;
        return newData;
      });
    },
    [
      takePagination,
      selectorCursor,
      selectorData.fromDate,
      selectorData.toDate,
      selectorData.concessionId,
      selectorData.name,
      selectorData.state,
      selectorData.teamIds,
    ]
  );

  const onChangePKInit = useCallback<onChangeInputPKTypes>(
    (km, meter) => {
      setSelectorData((prevData) => ({ ...prevData, pkInitKm: km, pkInitMeter: meter }));
    },
    []
  );

  const onChangePKEnd = useCallback<onChangeInputPKTypes>(
    (km, meter) => {
      setSelectorData((prevData) => ({ ...prevData, pkEndKm: km, pkEndMeter: meter }));
    },
    []
  );

  const onClearValue = useCallback(() => {
    inputDateRef.current?.clearSelected();
    inputStateRef.current?.clearSelected();
    inputNameRef.current?.clear();
    inputTeamRef.current?.clearSelected();
    inputTrackRef.current?.clearSelected();
    inputListPredefinedRef.current?.clearSelected();
    inputPKInitRef.current?.clearSelected();
    inputPKEndRef.current?.clearSelected();
    setColumnFilters([]);

    // Check if there's only one concession
    const hasOnlyOneConcession = concessionOptions && concessionOptions.length === 1;
    const onlyConcessionId = hasOnlyOneConcession ? concessionOptions[0].value : undefined;

    // If there's only one concession, keep it selected
    // Otherwise, clear it
    if (!hasOnlyOneConcession) {
      inputListConRef.current?.clearSelected();
    }

    const dataCursor = {
      take: takePagination,
      cursor: '',
      concessionId: hasOnlyOneConcession ? onlyConcessionId : undefined,
      fromDate: undefined,
      toDate: undefined,
      orderBy: 'desc',
      state: undefined,
      name: undefined,
      teamIds: undefined,
      predefinedOrderId: undefined,
      track: undefined,
      pkInitKm: undefined,
      pkInitMeter: undefined,
      pkEndKm: undefined,
      pkEndMeter: undefined,
    };
    dispatch(setCursor(tableId, dataCursor));
    setSelectorCursor((prevData) => ({
      ...prevData,
      concessionId: hasOnlyOneConcession ? onlyConcessionId : undefined,
      fromDate: undefined,
      toDate: undefined,
      name: undefined,
      state: undefined,
      teamIds: undefined,
      predefinedOrderId: undefined,
      track: undefined,
      pkInitKm: undefined,
      pkInitMeter: undefined,
      pkEndKm: undefined,
      pkEndMeter: undefined,
    }));
    setSelectorData((prevData) => ({
      ...prevData,
      concessionId: hasOnlyOneConcession ? onlyConcessionId : undefined,
      fromDate: undefined,
      toDate: undefined,
      name: undefined,
      state: undefined,
      teamIds: undefined,
      predefinedOrderId: undefined,
      track: undefined,
      pkInitKm: undefined,
      pkInitMeter: undefined,
      pkEndKm: undefined,
      pkEndMeter: undefined,
    }));
    setCurrentServerPage(1);
    onReFetchPagination();
    // Tarea #11 fix: `takePagination` debe estar en deps para evitar stale closure.
  }, [concessionOptions, takePagination]);

  const handleClickRow = useCallback(
    (row: Row<dataTable>) => {
      // Dispatch to Redux immediately (like InputRadioDefaultTable)
      // This ensures the data is in Redux before onDoubleTapAccept is called
      if (namingPersistForm) {
        dispatch(selectedRowModalTable(namingPersistForm, row.original));
      }
      const val: boolean = row.getIsSelected();
      if (!val) {
        setRowState(row.original || {});
      } else {
        setRowState({});
      }
    },
    [namingPersistForm, dispatch, setRowState]
  );

  // Sync rowSelection → rowState for direct radio button clicks (stopPropagation prevents onSingleTap)
  useEffect(() => {
    const selectedIds = Object.keys(rowSelection).filter((key) => (rowSelection as Record<string, boolean>)[key]);
    if (selectedIds.length > 0) {
      const selectedRow = table.getRow(selectedIds[0]);
      if (selectedRow) {
        if (namingPersistForm) {
          dispatch(selectedRowModalTable(namingPersistForm, selectedRow.original));
        }
        setRowState(selectedRow.original || {});
      }
    } else {
      setRowState({});
    }
  }, [rowSelection, setRowState, table, namingPersistForm, dispatch]);

  const hiddenColumns: number = Object.values(
    columnVisibility as object
  ).reduce((count, currentValue) => (!currentValue ? count + 1 : count), 0);

  // commonColumns definition removed

  // commonLabels definition removed

  // Auto-select the only concession if there's only one available
  useEffect(() => {
    if (
      concessionOptions &&
      concessionOptions.length === 1 &&
      !selectorCursor.concessionId
    ) {
      const onlyConcession = concessionOptions[0];
      setSelectorCursor((prev) => ({
        ...prev,
        concessionId: onlyConcession.value,
      }));
      setSelectorData((prev) => ({
        ...prev,
        concessionId: onlyConcession.value,
      }));
    }
  }, [concessionOptions, selectorCursor.concessionId]);

  const fetchCampaignID = useCallback(
    async (id: string, take = takePagination) => {
      const dataSelector = {
        take,
        cursor: id,
        concessionId:
          selectorData?.concessionId ?? selectorCursor?.concessionId,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        orderBy: 'desc',
        name: selectorData.name ?? selectorCursor.name,
        state: selectorData.state ?? selectorCursor.state,
        teamIds: selectorData.teamIds ?? selectorCursor.teamIds,
        predefinedOrderId:
          selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
        track: selectorData.track ?? selectorCursor.track,
        pkInitKm: selectorData?.pkInitKm ?? selectorCursor?.pkInitKm,
        pkInitMeter: selectorData?.pkInitMeter ?? selectorCursor?.pkInitMeter,
        pkEndKm: selectorData?.pkEndKm ?? selectorCursor?.pkEndKm,
        pkEndMeter: selectorData?.pkEndMeter ?? selectorCursor?.pkEndMeter,
      };

      if (take > 0) {
        const lastCampaign =
          dataAll[dataAll.length - 1].genericOrCampaignOrderID;
        if (lastCampaign) {
          dispatch(setCursor(tableId, dataSelector));
        }
      } else {
        const firstAccident = dataAll[0].genericOrCampaignOrderID;
        if (firstAccident) {
          dispatch(setCursor(tableId, dataSelector));
        }
      }

      return dataSelector;
    },
    [
      takePagination,
      selectorCursor,
      selectorData?.concessionId,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData.name,
      selectorData.state,
      dataAll,
    ]
  );

  const nextPagination = async () => {
    // Clamp defensivo contra bug off-by-one (backend/cache hasNextPage true en última real).
    const pageCount = Math.max(
      1,
      Math.ceil((data?.totalCount ?? 0) / takePagination),
    );
    if (
      dataAll.length >= 1 &&
      data.pageInfo.hasNextPage &&
      currentServerPage < pageCount
    ) {
      setCurrentServerPage((prev) => Math.min(prev + 1, pageCount));
      await fetchCampaignID(
        dataAll[dataAll.length - 1].genericOrCampaignOrderID as string
      );
      setIsLoading(true);
      onReFetchPagination();
    }
  };

  const backPagination = async () => {
    if (dataAll.length >= 1 && data.pageInfo.hasPreviousPage) {
      setCurrentServerPage((prev) => Math.max(1, prev - 1));
      await fetchCampaignID(
        dataAll[0].genericOrCampaignOrderID as string,
        -takePagination
      );
      setIsLoading(true);
      onReFetchPagination();
    }
  };

  const handleSearch = async () => {
    const dataCursor = {
      take: takePagination,
      cursor: '',
      concessionId: selectorData.concessionId ?? selectorCursor.concessionId,
      fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
      toDate: selectorData?.toDate ?? selectorCursor?.toDate,
      name: selectorData.name ?? selectorCursor.name,
      state: selectorData.state ?? selectorCursor.state,
      teamIds: selectorData.teamIds ?? selectorCursor.teamIds,
      predefinedOrderId:
        selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
      track: selectorData.track ?? selectorCursor.track,
      pkInitKm: selectorData?.pkInitKm ?? selectorCursor?.pkInitKm,
      pkInitMeter: selectorData?.pkInitMeter ?? selectorCursor?.pkInitMeter,
      pkEndKm: selectorData?.pkEndKm ?? selectorCursor?.pkEndKm,
      pkEndMeter: selectorData?.pkEndMeter ?? selectorCursor?.pkEndMeter,
      orderBy: 'desc',
    };
    dispatch(setCursor(tableId, dataCursor));
    setCurrentServerPage(1);

    setIsLoading(true);
    onReFetchPagination();
  };

  const filterContainerRef = useRef<HTMLDivElement>(null);
  useFilterKeyboardShortcuts(filterContainerRef, handleSearch, onClearValue, isLoading);

  return (
    <ContainerStyled>
      {isLoading && (
        <ContainerLoader>
          <Loader />
        </ContainerLoader>
      )}
      <FiltersWithLoadingStyled isLoading={isLoading}>
        <SubContainerHead>
          <Title component={'h2'} variant={'semiBold'} size={'M'}>
            {titleText}
          </Title>
          {isLandscapeTablet && (
            <ShowFiltersButton isBlack={showFilters} onClick={handleClick}>
              <ArrowBottomStyled isRotate={showFilters} />
            </ShowFiltersButton>
          )}
        </SubContainerHead>
        {showFilters && (
          <SubContainerFiltersStyled ref={filterContainerRef}>
            <CampaignSearchContainer>
              <StyledTypography size="S">
                {t('tables:headers.name')}
              </StyledTypography>
              <SearchBar
                variant="campaign"
                onChangeInput={onChangeCampaign}
                ref={inputNameRef}
                disabled={isLoading}
              />
            </CampaignSearchContainer>
            <InputCalendarStyled
              ref={inputDateRef}
              label={t('tables:filters.dateRangeFilter')}
              name={'data'}
              onChangeInput={onChangeDateInput}
              dateRange={
                selectorCursor?.fromDate && selectorCursor?.toDate
                  ? {
                    from: parseISO(selectorCursor?.fromDate),
                    to: parseISO(selectorCursor?.toDate),
                  }
                  : undefined
              }
              disabled={isLoading}
            />
            {concessionOptions && concessionOptions.length > 1 && (
              <DropDownInput
                inputSize="XS"
                ref={inputListConRef}
                labelText={t('tables:headers.concession')}
                placeholder={t('common:placeholders.select')}
                typeDropDown={'Secondary'}
                defaultValue={selectorCursor.concessionId ?? ''}
                options={concessionOptions as Options}
                onChangeSelected={onChangeSelectedConcession as onChangeValue}
                isDisabled={isLoading}
              />
            )}
            <DropDownInput
              inputSize="S"
              ref={inputStateRef}
              labelText={t('tables:headers.status')}
              placeholder={t('common:placeholders.select')}
              typeDropDown={'Secondary'}
              defaultValue={selectorCursor.state ?? ''}
              options={roleActive ? getStateOptionsByRoleAndEntityType(t, roleActive as Role, TABLE_ENTITY_TYPES[tableId as string], listingFiltersByRole(tableId)) : []}
              onChangeSelected={onChangeSelectedState as onChangeValue}
              isDisabled={isLoading}
            />
            {teamOptions && teamOptions.length > 0 && (
              <MultiSelectDropdown
                inputSize="M"
                ref={inputTeamRef}
                labelText={t('forms:workOrder.team')}
                placeholder={t('common:placeholders.select')}
                typeDropDown={'Secondary'}
                selectedValues={selectorCursor?.teamIds ?? []}
                options={teamOptions as Options}
                onChangeSelected={onChangeSelectedTeam}
                isDisabled={isLoading}
              />
            )}
            <DropDownInput
              inputSize="XS"
              ref={inputTrackRef}
              labelText={t('forms:workOrder.roadType')}
              placeholder={t('common:placeholders.select')}
              typeDropDown={'Secondary'}
              defaultValue={selectorCursor.track ?? ''}
              options={trackOptions as Options}
              onChangeSelected={onChangeSelectedTrack as onChangeValue}
              isDisabled={isLoading}
            />
            {predefinedOrderOptions && predefinedOrderOptions.length > 0 && (
              <SearchableDropdownStyled
                inputSize="L"
                ref={inputListPredefinedRef}
                labelText={t('forms:workOrder.predefined')}
                placeholder={t('common:placeholders.select')}
                typeDropDown={'Default'}
                defaultValue={selectorCursor?.predefinedOrderId ?? ''}
                options={predefinedOrderOptions as Options}
                onChangeSelected={onChangeSelectedPredefinedOrder as onChangeValue}
                isDisabled={isLoading}
              />
            )}
            <InputPK
              label={t('tables:headers.pkInit')}
              disabled={isLoading}
              onChangeInputPK={onChangePKInit}
              ref={inputPKInitRef}
            />
            <InputPK
              label={t('tables:headers.pkEnd')}
              disabled={isLoading}
              onChangeInputPK={onChangePKEnd}
              ref={inputPKEndRef}
            />
            <ContainerButtonStyled>
              <ButtonIconBackground
                variant={'delete'}
                onClick={onClearValue}
                disabled={isLoading}
                title={`${t('tables:filters.clearFilters')} (Shift+Enter)`}
              />
              <DropDownVisibilityColumn
                columnNames={Object.fromEntries(table.getAllColumns().slice(1).map(c => [c.id, translatedColumnNames[c.id as keyof typeof translatedColumnNames]?.label || c.id]))}
                columns={table.getAllColumns().slice(1)}
                isDisabled={isLoading}
              />
              <ButtonIconBackground
                variant={'restart'}
                onClick={() => { setCurrentServerPage(1); onReFetchData?.(); }}
                disabled={isLoading}
              />
              <ButtonIconBackground
                variant={'search'}
                onClick={handleSearch}
                disabled={isLoading}
                title={`${t('buttons:search')} (Enter)`}
              />
            </ContainerButtonStyled>
          </SubContainerFiltersStyled>
        )}
      </FiltersWithLoadingStyled>
      <TableContainerStyled isLoading={isLoading}>
        <Table {...props}>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <Th key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                  </Th>
                ))}
              </tr>
            ))}
          </thead>
          <Tbody>
            {table.getRowModel().rows.map((row) => {
              return (
                <DoubleTapRow
                  key={row.id}
                  isDisabled={isLoading || (disabledRowStates?.includes(row.original.state ?? '') ?? false)}
                  onSingleTap={() => {
                    handleClickRow(row);
                    row.getToggleSelectedHandler()(row);
                  }}
                  onDoubleTap={() => {
                    handleClickRow(row);
                    row.getToggleSelectedHandler()(row);
                    if (onDoubleTapAccept) {
                      onDoubleTapAccept();
                    }
                  }}
                  StyledComponent={TrStyled}
                  componentProps={{
                    checked: row.getIsSelected(),
                    indeterminate: !table.getIsSomeRowsSelected(),
                  }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TdStyled
                      column={cell.column.id as columnID}
                      hiddenColumns={hiddenColumns}
                      key={cell.id}
                    >
                      <>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </>
                    </TdStyled>
                  ))}
                </DoubleTapRow>
              );
            })}
          </Tbody>
        </Table>
      </TableContainerStyled>
      {showButtonsPagination && (
        <PaginationButtons
          pageCount={Math.max(1, Math.ceil((data?.totalCount ?? 0) / takePagination))}
          currentPage={currentServerPage}
          totalCount={data?.totalCount}
          nextPage={nextPagination}
          previousPage={backPagination}
          isDisabled={isLoading}
        />
      )}
    </ContainerStyled>
  );
};
